import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import { ConsoleTelemetryAdapter } from './ConsoleTelemetryAdapter.ts';
import {
  exportOtlpLog,
  exportOtlpSpan,
  newTraceIds,
  type OtlpExportConfig,
} from './otlpHttpExport.ts';

/** OTLP/HTTP export for prod; mirrors console when endpoint missing (local dev). */
export class OtelTelemetryAdapter implements TelemetryPort {
  private readonly console = new ConsoleTelemetryAdapter();
  private readonly otlp: OtlpExportConfig | null;

  constructor(config: { endpoint: string; serviceName: string; headers?: Record<string, string> }) {
    const endpoint = config.endpoint.trim();
    this.otlp = endpoint
      ? { endpoint, serviceName: config.serviceName.trim() || 'esan-paquetes', headers: config.headers }
      : null;
  }

  logInfo(message: string, context?: Record<string, unknown>): void {
    this.console.logInfo(message, context);
    this.exportLog('INFO', message, context);
  }

  logError(error: Error | string, context?: Record<string, unknown>): void {
    this.console.logError(error, context);
    const msg = error instanceof Error ? error.message : String(error);
    this.exportLog('ERROR', msg, {
      ...context,
      errorName: error instanceof Error ? error.name : undefined,
    });
  }

  trackEvent(eventName: string, payload?: Record<string, unknown>): void {
    this.console.trackEvent(eventName, payload);
    this.exportLog('DEBUG', eventName, { eventName, ...payload });
  }

  setUserContext(user: { id: string; role?: string; [key: string]: unknown } | null): void {
    this.console.setUserContext(user);
  }

  logHttpRequest(meta: { correlationId: string; method: string; url: string }): void {
    this.console.logHttpRequest(meta);
    this.exportLog('INFO', 'http.request', { type: 'http.request', ...meta });
  }

  logHttpResponse(meta: { correlationId: string; status: number; durationMs: number; url: string }): void {
    this.console.logHttpResponse(meta);
    this.exportLog('INFO', 'http.response', { type: 'http.response', ...meta });
  }

  startTrace(spanName: string, options?: { correlationId?: string }): { end: () => void; fail: (err: Error) => void } {
    const otlp = this.otlp;
    if (!otlp) return this.console.startTrace();
    const ids = newTraceIds();
    let ended = false;
    const finish = (ok: boolean) => {
      if (ended) return;
      ended = true;
      exportOtlpSpan(otlp, {
        ...ids,
        name: spanName,
        endUnixNano: `${BigInt(Date.now()) * 1_000_000n}`,
        ok,
        correlationId: options?.correlationId,
      });
    };
    return { end: () => finish(true), fail: () => finish(false) };
  }

  private exportLog(severity: 'DEBUG' | 'INFO' | 'ERROR', message: string, context?: Record<string, unknown>): void {
    if (!this.otlp) return;
    const { correlationId, ...rest } = context ?? {};
    const attrs: Record<string, string> = { message };
    if (typeof correlationId === 'string') attrs.correlationId = correlationId;
    for (const [k, v] of Object.entries(rest)) {
      if (v == null) continue;
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
        attrs[k] = String(v);
      }
    }
    exportOtlpLog(this.otlp, severity, JSON.stringify({ message, ...rest }), attrs);
  }
}
